/**
 * OneOnOneCoachingSection — E18-1
 * Renders the 1:1 Coaching modality section in the Sessions tab.
 * Supports list view (one row per participant) and card view (3-column grid).
 * Used in both the "All" tab (accordion/preview mode) and the "1:1 Coaching" tab (full mode).
 */

import { useState, useMemo, type ReactNode } from 'react';
import {
  Box,
  Group,
  Text,
  ThemeIcon,
  Avatar,
  Badge,
  ActionIcon,
  Collapse,
  Button,
  Stack,
  Divider,
  Progress,
  Tooltip,
  Menu,
  Select,
} from '@mantine/core';
import { User, ChevronDown, ChevronRight, List, MoreHorizontal, Plus, Send, Check, Clock, Grid, Edit3 } from 'react-feather';
import { isCheckinOneOnOneType } from '../../../../types';
import type { OneOnOneParticipant, OneOnOneSessionSlot, OneOnOneType } from '../../../../types';
import { formatDate, formatTime, formatTimeRange } from '../../../../utils/formatters';
import { getDisplayTimezone } from '../../../../utils/timezone';
import { ParticipantAvatar } from '../../../../components/common/ParticipantAvatar';
import { ButtonToggle } from '../../../../components/common/ButtonToggle';
import { getAvatarColor } from '../../../../utils/avatarHelpers';
import { getGroupAvatarColor } from '../../../../utils/groupColors';
import {
  getOneOnOneTypeConfig,
  ONE_ON_ONE_TYPE_FILTER_OPTIONS,
} from '../../../../config/oneOnOneTypeConfig';
import { formatCardDate, getCompletion, isPastAwaitingAttendance } from './sessionsHelpers';
import styles from './OneOnOneCoachingSection.module.css';

// MED-1350: short block date ("Jan 14"), in the same timezone as formatDate.
function formatShortDate(date: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: getDisplayTimezone(),
  });
}

/**
 * MED-1350: the block's own coach, only when it differs from the row's coach.
 * A check-in takes the group coach, so the row's COACH label can name someone
 * else.
 */
function blockCoachName(
  session: OneOnOneSessionSlot,
  participant: OneOnOneParticipant,
): string | null {
  if (!session.coach_id || !session.coach_name) return null;
  return session.coach_id === participant.coach_id ? null : session.coach_name;
}

/**
 * MED-1350: the click handler for one block, or undefined for a block the
 * viewer cannot act on. A block with no date opens the date editor, so it
 * gets no handler when canEditSession refuses it. A dated block keeps its
 * handler, which opens the read-only drawer.
 */
function blockClickHandler(
  participant: OneOnOneParticipant,
  session: OneOnOneSessionSlot,
  onSessionClick?: (participant: OneOnOneParticipant, session: OneOnOneSessionSlot) => void,
  canEditSession?: (session: OneOnOneSessionSlot) => boolean,
): (() => void) | undefined {
  if (!onSessionClick) return undefined;
  // MED-1394: a withdrawn participant gets no "+ Add Date" booking action.
  if (!session.scheduled_start && participant.enrollment_status === 'withdrawn') return undefined;
  if (!session.scheduled_start && canEditSession && !canEditSession(session)) return undefined;
  return () => onSessionClick(participant, session);
}

// ─── Session Pill (List View) ────────────────────────────────────────────────

interface SessionPillProps {
  session: OneOnOneSessionSlot;
  onClick?: () => void;
  /** MED-1350: the block's coach when it differs from the row's coach */
  coachName?: string | null;
}

function SessionPill({ session, onClick, coachName }: SessionPillProps) {
  // MED-1360: a past scheduled block with no attendance takes the amber
  // "needs attendance" look that a completed, unmarked block has.
  const isCompleted = session.status === 'completed' || isPastAwaitingAttendance(session);
  const hasDate = !!session.scheduled_start;
  const isCheckin = isCheckinOneOnOneType(session.one_on_one_type);
  const typeConfig = getOneOnOneTypeConfig(session.one_on_one_type);
  const label = isCheckin
    ? typeConfig.chipLabel.toUpperCase()
    : session.session_number != null
      ? `SESSION ${session.session_number}`
      : 'SESSION';
  // MED-1350: a read-only block (no click handler) shows no "+ Add Date" prompt.
  const dateLabel = hasDate
    ? formatShortDate(session.scheduled_start!)
    : onClick
      ? '+ Add Date'
      : 'No date';
  const awaitingAttendance = isCompleted && !session.attendance_marked;

  // MED-1350: fixed block sizes. A session is 104 x 76 px, a check-in 92 x 56 px.
  // A block that names its coach keeps the width and may grow taller, so a
  // two-line name never clips.
  const blockHeight = isCheckin ? 56 : 76;
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: isCheckin ? 92 : 104,
    ...(coachName ? { minHeight: blockHeight } : { height: blockHeight }),
    padding: coachName ? '6px 8px' : '0 8px',
    overflow: 'hidden',
    borderRadius: 6,
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none',
    border: isCompleted ? 'none' : '1px solid #D0CEC9',
    backgroundColor: isCompleted
      ? awaitingAttendance ? '#E9A23B' : '#4A7C59'
      : hasDate ? '#F0FAF3' : 'transparent',
  };

  const tooltipLabel = (
    <Box>
      <Text size="xs" fw={700}>{isCheckin ? typeConfig.displayLabel : typeConfig.fullLabel}</Text>
      <Text size="xs">
        {isCheckin ? '' : session.session_number != null ? `Session ${session.session_number} · ` : 'Session · '}
        {hasDate ? formatDate(session.scheduled_start) : 'Not scheduled'}
      </Text>
      {coachName && <Text size="xs">Coach: {coachName}</Text>}
      {awaitingAttendance && <Text size="xs">Needs attendance</Text>}
      {!typeConfig.isStandard && (
        <Text size="xs" c="dimmed" mt={2}>{typeConfig.description}</Text>
      )}
    </Box>
  );

  return (
    <Tooltip label={tooltipLabel} withArrow position="top" multiline w={240}>
      <Box style={baseStyle} onClick={onClick}>
        <Text
          size="10px"
          fw={700}
          style={{
            letterSpacing: '0.4px',
            color: isCompleted ? '#fff' : '#555',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Text>
        <Text
          size="11px"
          fw={500}
          c={isCompleted ? 'white' : hasDate ? 'dimmed' : 'dimmed'}
          style={{ lineHeight: 1.3, marginTop: 2, whiteSpace: 'nowrap' }}
        >
          {dateLabel}
        </Text>
        {/* MED-1394: the start time and zone, as the card view shows. */}
        {hasDate && (
          <Text
            size="10px"
            fw={500}
            c={isCompleted ? 'white' : 'dimmed'}
            style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}
          >
            {formatTime(session.scheduled_start, session.timezone)}
          </Text>
        )}
        {/* MED-1350: name the block's coach quietly when it is not the row's coach. */}
        {coachName && (
          <Text
            size="10px"
            c={isCompleted ? undefined : 'dimmed'}
            // MED-1350: the full name wraps onto two lines inside the block.
            style={{
              lineHeight: 1.2,
              marginTop: 1,
              maxWidth: '100%',
              textAlign: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: isCompleted ? 'rgba(255,255,255,0.85)' : undefined,
            }}
          >
            {coachName}
          </Text>
        )}
        {/* AC1/AC3: type chip, neutral for standard, accent for non-standard.
            MED-1350: a check-in block has no type chip. */}
        {!isCheckin && (
          <Badge
            size="xs"
            radius="sm"
            variant={isCompleted ? 'filled' : typeConfig.badgeVariant}
            color={isCompleted ? 'gray' : typeConfig.badgeColor}
            mt={3}
            style={{
              fontWeight: 700,
              letterSpacing: '0.3px',
              backgroundColor: isCompleted ? 'rgba(255,255,255,0.25)' : undefined,
              color: isCompleted ? '#fff' : undefined,
            }}
          >
            {typeConfig.chipLabel}
          </Badge>
        )}
        {isCompleted && (
          <Group gap={4} mt={3} justify="center">
            {session.attendance_marked ? (
              <Check size={9} color="#fff" />
            ) : (
              <Clock size={9} color="#fff" />
            )}
          </Group>
        )}
      </Box>
    </Tooltip>
  );
}

// ─── Completion Badge ─────────────────────────────────────────────────────────

interface CompletionBadgeProps {
  completed: number;
  total: number;
  label: string;
}

function CompletionBadge({ completed, total, label }: CompletionBadgeProps) {
  const isFullyComplete = total > 0 && completed === total;
  return (
    <Badge
      size="sm"
      radius="sm"
      variant="filled"
      color={isFullyComplete ? 'medleyGreen' : 'gray'}
      style={{ minWidth: 100, fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {completed}/{total} {label}
    </Badge>
  );
}

// ─── Coach Cell (AC3, AC4) ──────────────────────────────────────────────────

interface CoachCellProps {
  coachName: string | null;
  coachAvatarUrl: string | null;
  onAssign?: () => void;
}

function CoachCell({ coachName, coachAvatarUrl, onAssign }: CoachCellProps) {
  if (!coachName) {
    return (
      <Group
        gap="xs"
        wrap="nowrap"
        style={{
          backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
          borderRadius: 6,
          padding: '8px 14px',
          minWidth: 160,
        }}
      >
        <Box
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '2px dashed #E9A23B',
            backgroundColor: '#FFF8EE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: onAssign ? 'pointer' : 'default',
            flexShrink: 0,
          }}
          onClick={onAssign}
        >
          <Plus size={12} color="#E9A23B" />
        </Box>
        <Box>
          <Text size="9px" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.3px' }}>
            Coach
          </Text>
          <Text
            size="sm"
            fw={500}
            c="orange"
            style={{ cursor: onAssign ? 'pointer' : 'default' }}
            onClick={onAssign}
          >
            Unassigned
          </Text>
        </Box>
      </Group>
    );
  }

  // AC3: Use standard coach avatar color coding
  const coachAvatarBgColor = getAvatarColor(coachName);
  const coachInitials = coachName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // AC4: Match group coaching list header style — pill with background, COACH label, name
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      style={{
        backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
        borderRadius: 6,
        padding: '8px 14px',
        minWidth: 160,
      }}
    >
      <Avatar
        src={coachAvatarUrl}
        size="sm"
        radius="xl"
        styles={{
          root: { backgroundColor: coachAvatarBgColor },
          placeholder: { color: '#FFFFFF', backgroundColor: coachAvatarBgColor },
        }}
      >
        {coachInitials}
      </Avatar>
      <Box>
        <Text size="9px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
          Coach
        </Text>
        <Text size="sm" fw={500}>
          {coachName}
        </Text>
      </Box>
    </Group>
  );
}

// ─── Group Pill (AC9) ───────────────────────────────────────────────────────

interface GroupPillProps {
  groupName: string | null;
  groupId: string | null;
  /** Text in place of the group name. The dot color stays the group's. */
  label?: string;
  title?: string;
}

export function GroupPill({ groupName, groupId, label, title }: GroupPillProps) {
  if (!groupName) return null;
  const dotColor = getGroupAvatarColor(groupName, groupId || '');
  return (
    <Badge
      className={styles.groupPill}
      title={title ?? groupName}
      variant="light"
      color="medleyGreen"
      leftSection={
        <Box
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: dotColor,
          }}
        />
      }
      tt="none"
      size="xs"
    >
      {label ?? groupName}
    </Badge>
  );
}

// ─── Withdrawn label (MED-1350) ─────────────────────────────────────────────

/**
 * A quiet label for a withdrawn participant. The card view shows it in the
 * name line. The list view shows it on its own line under the name, because
 * the list name column has a fixed width and the label must never truncate
 * the name.
 */
function WithdrawnLabel({ participant }: { participant: OneOnOneParticipant }) {
  if (participant.enrollment_status !== 'withdrawn') return null;
  return (
    <Badge className={styles.withdrawnLabel} variant="light" color="gray" tt="none" size="xs">
      Withdrawn
    </Badge>
  );
}

const isWithdrawn = (participant: OneOnOneParticipant) =>
  participant.enrollment_status === 'withdrawn';

// ─── List View Row (AC1, AC2, AC5, AC6, AC7, AC9) ──────────────────────────

interface ParticipantRowProps {
  participant: OneOnOneParticipant;
  onSessionClick?: (participant: OneOnOneParticipant, session: OneOnOneSessionSlot) => void;
  onAssignCoach?: (participant: OneOnOneParticipant) => void;
  onEditCoachAssignment?: (participant: OneOnOneParticipant) => void;
  canEditSession?: (session: OneOnOneSessionSlot) => boolean;
}

function ParticipantRow({
  participant,
  onSessionClick,
  onAssignCoach,
  onEditCoachAssignment,
  canEditSession,
}: ParticipantRowProps) {
  const fullName = `${participant.first_name} ${participant.last_name}`;
  const completion = getCompletion(participant);

  // MED-1350: the layout (inline strip, strip under the name, sideways scroll)
  // lives in OneOnOneCoachingSection.module.css.
  return (
    <Box className={styles.row}>
      {/* Participant info: AC2 standard avatar, AC6 group pill inline, AC7 role beneath */}
      <Box className={styles.who}>
        <ParticipantAvatar
          firstName={participant.first_name}
          lastName={participant.last_name}
          size={40}
        />
        <Box className={styles.whoText}>
          <Box className={styles.nameLine}>
            <Text fw={600} size="sm" className={styles.name} title={fullName}>{fullName}</Text>
            <GroupPill groupName={participant.group_name} groupId={participant.group_id} />
          </Box>
          {/* The list name column is a fixed 220 px, so the label takes its own line. */}
          {isWithdrawn(participant) && (
            <Box className={styles.withdrawnLine}>
              <WithdrawnLabel participant={participant} />
            </Box>
          )}
          {participant.job_title && (
            <Text size="12px" c="dimmed" className={styles.jobTitle}>{participant.job_title}</Text>
          )}
        </Box>
      </Box>

      {/* Session and check-in blocks, in the API order */}
      <Box className={styles.strip} style={isWithdrawn(participant) ? { opacity: 0.6 } : undefined}>
        {participant.sessions.map((session) => (
          <SessionPill
            key={session.id}
            session={session}
            onClick={blockClickHandler(participant, session, onSessionClick, canEditSession)}
            coachName={blockCoachName(session, participant)}
          />
        ))}
      </Box>

      {/* MED-1350: badge, coach and menu stay together on one line, so the
          menu never wraps under the badge on a phone. */}
      <Box className={styles.meta}>
      {/* Completion badge */}
      <Box className={styles.badge}>
        <CompletionBadge
          completed={completion.done}
          total={completion.total}
          label={completion.label}
        />
      </Box>

      {/* Coach: AC3 + AC4, natural width */}
      <Box className={styles.coach}>
        <CoachCell
          coachName={participant.coach_name}
          coachAvatarUrl={participant.coach_avatar_url}
          onAssign={onAssignCoach ? () => onAssignCoach(participant) : undefined}
        />
      </Box>

      {/* Kebab menu: AC1 "Edit Coach Assignment". MED-1350: shown only when the
          action is wired (the coach view has none). An empty box of the icon's
          size keeps the column layout stable. */}
      <Box className={styles.kebab}>
        {onEditCoachAssignment ? (
          <Menu shadow="md" width={220} position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <MoreHorizontal size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<Edit3 size={14} />}
                onClick={() => onEditCoachAssignment(participant)}
              >
                Edit Coach Assignment
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Box w={28} h={28} aria-hidden />
        )}
      </Box>
      </Box>
    </Box>
  );
}

// ─── Card View (AC2, AC3, AC4, AC9) ─────────────────────────────────────────

interface ParticipantCardProps {
  participant: OneOnOneParticipant;
  onSessionClick?: (participant: OneOnOneParticipant, session: OneOnOneSessionSlot) => void;
  onAssignCoach?: (participant: OneOnOneParticipant) => void;
  canEditSession?: (session: OneOnOneSessionSlot) => boolean;
}

function getSessionStatusBadge(session: OneOnOneSessionSlot): { label: string; color: string; variant: 'filled' | 'outline' | 'light' } {
  // MED-1360: past its time with no attendance, so not "SCHEDULED" any more.
  if (isPastAwaitingAttendance(session)) {
    return { label: 'NEEDS ATTENDANCE', color: 'warningAmber', variant: 'filled' };
  }
  if (session.status === 'completed') {
    if (!session.attendance_marked) {
      return { label: 'NEEDS REVIEW', color: 'warningAmber', variant: 'filled' };
    }
    return { label: 'COMPLETED', color: 'medleyGreen', variant: 'filled' };
  }
  if (session.status === 'cancelled' || session.status === 'rescheduled') {
    return { label: 'RESCHEDULE', color: 'dangerRed', variant: 'filled' };
  }
  return { label: 'SCHEDULED', color: 'blue', variant: 'light' };
}

function ParticipantCard({ participant, onSessionClick, onAssignCoach, canEditSession }: ParticipantCardProps) {
  const fullName = `${participant.first_name} ${participant.last_name}`;
  const completion = getCompletion(participant);
  const progressPct = completion.total > 0
    ? Math.round((completion.done / completion.total) * 100)
    : 0;
  const isFullyComplete = completion.total > 0 && completion.done === completion.total;

  // AC3: Coach avatar color
  const coachAvatarBgColor = participant.coach_name ? getAvatarColor(participant.coach_name) : undefined;
  const coachInitials = participant.coach_name
    ? participant.coach_name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '';

  return (
    <Box
      className={styles.card}
      style={{
        border: '1px solid #E5E2DC',
        borderRadius: 8,
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header — AC2: participant avatar, AC6: group pill inline, AC7: role beneath */}
      <Box p="md">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" wrap="nowrap" className={styles.cardHeadLeft}>
            <ParticipantAvatar
              firstName={participant.first_name}
              lastName={participant.last_name}
              size={44}
            />
            <Box className={styles.whoText}>
              {/* MED-1360: in a card the name wraps and the pill drops to the
                  next line, so a 300 px card cuts neither short. */}
              <Box className={`${styles.nameLine} ${styles.cardNameLine}`}>
                <Text fw={700} size="sm" className={styles.name} title={fullName}>{fullName}</Text>
                <GroupPill groupName={participant.group_name} groupId={participant.group_id} />
              </Box>
              {/* MED-1350: the label takes its own line, as in the list view,
                  so a long name, the group pill and the badge never push it out. */}
              {isWithdrawn(participant) && (
                <Box className={styles.withdrawnLine}>
                  <WithdrawnLabel participant={participant} />
                </Box>
              )}
              {participant.job_title && (
                <Text size="12px" c="dimmed" mt={2} className={styles.jobTitle}>{participant.job_title}</Text>
              )}
            </Box>
          </Group>
          <Badge
            size="sm"
            radius="sm"
            variant="filled"
            color={isFullyComplete ? 'medleyGreen' : 'gray'}
            style={{ fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {completion.done}/{completion.total} {completion.label}
          </Badge>
        </Group>
      </Box>

      <Divider color="#F0EDE8" />

      {/* Coach — AC3 + AC4: matching group coaching style */}
      <Box px="md" py="sm">
        <Text size="10px" c="dimmed" fw={600} mb={6} style={{ letterSpacing: '0.3px', textTransform: 'uppercase' }}>
          Coach
        </Text>
        {participant.coach_name ? (
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Avatar
                src={participant.coach_avatar_url}
                size={32}
                radius="xl"
                styles={{
                  root: { backgroundColor: coachAvatarBgColor },
                  placeholder: { color: '#FFFFFF', backgroundColor: coachAvatarBgColor },
                }}
              >
                {coachInitials}
              </Avatar>
              <Text size="sm" fw={500}>{participant.coach_name}</Text>
            </Group>
            {/* MED-1350: the coach view passes no handler, so it gets no dead control. */}
            {onAssignCoach && (
              <Button size="xs" variant="default" radius="sm" onClick={() => onAssignCoach(participant)}>Change</Button>
            )}
          </Group>
        ) : (
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '2px dashed #E9A23B',
                  backgroundColor: '#FFF8EE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: onAssignCoach ? 'pointer' : 'default',
                  flexShrink: 0,
                }}
                onClick={onAssignCoach ? () => onAssignCoach(participant) : undefined}
              >
                <Plus size={12} color="#E9A23B" />
              </Box>
              <Text size="sm" c="dimmed">Unassigned</Text>
            </Group>
          </Group>
        )}
      </Box>

      <Divider color="#F0EDE8" />

      {/* Sessions */}
      <Stack gap={0} style={isWithdrawn(participant) ? { opacity: 0.6 } : undefined}>
        {participant.sessions.length === 0 ? (
          <Text size="sm" c="dimmed" p="md">No sessions configured</Text>
        ) : (
          participant.sessions.map((session) => {
            const statusBadge = getSessionStatusBadge(session);
            const hasDate = !!session.scheduled_start;
            const isCheckin = isCheckinOneOnOneType(session.one_on_one_type);
            const typeConfig = getOneOnOneTypeConfig(session.one_on_one_type);
            const sessionLabel = isCheckin
              ? typeConfig.chipLabel
              : session.session_number != null
                ? `Session ${session.session_number}`
                : 'Session';
            const coachName = blockCoachName(session, participant);
            const onRowClick = blockClickHandler(participant, session, onSessionClick, canEditSession);

            // MED-1350: row height, 60 px for a session, 48 px for a check-in.
            // MED-1360: a row may grow past it, so a coach name or a wrapped
            // date never clips. A row that needs no more space keeps the height.
            const rowHeight = isCheckin ? 48 : 60;
            return (
              <Group
                key={session.id}
                justify="space-between"
                px="md"
                mih={rowHeight}
                py={6}
                gap={8}
                wrap="nowrap"
                style={{
                  borderBottom: '1px solid #F5F3EF',
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
                onClick={onRowClick}
              >
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                  <Text size="xs" c="dimmed" fw={600} w={12} style={{ flexShrink: 0 }}>
                    {isCheckin ? '' : session.session_number}
                  </Text>
                  <Box style={{ minWidth: 0 }}>
                    {hasDate ? (
                      <>
                        <Text size="sm" fw={600} className={styles.cardRowText} style={{ lineHeight: 1.3 }}>
                          {formatCardDate(session.scheduled_start!)}
                        </Text>
                        {session.scheduled_end && (
                          <Text size="11px" c="dimmed" className={styles.cardRowTime}>
                            {formatTimeRange(session.scheduled_start!, session.scheduled_end, session.timezone)}
                          </Text>
                        )}
                      </>
                    ) : (
                      // MED-1360: "+ Add Date" moves to a second line at a narrow
                      // card width instead of being cut short.
                      <Text size="sm" c="dimmed" className={styles.cardRowLabel}>
                        {sessionLabel} ·{' '}
                        {onRowClick ? (
                          <Text span c="blue" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Add Date</Text>
                        ) : (
                          <Text span c="dimmed" style={{ whiteSpace: 'nowrap' }}>No date</Text>
                        )}
                      </Text>
                    )}
                    {/* MED-1350: the block's coach, when it is not the card's
                        coach, on its own muted line for every row type. */}
                    {coachName && (
                      <Text size="11px" c="dimmed" className={styles.cardRowCoach}>
                        {coachName}
                      </Text>
                    )}
                  </Box>
                </Group>
                {/* MED-1350: an undated check-in row has no right side. Its
                    title already names it, and the space keeps the add-date
                    link from clipping. */}
                {!(isCheckin && !hasDate) && (
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  {isCheckin ? (
                    // MED-1350: a dated check-in shows its name in the chip
                    // column at chip size, as dimmed text, with no badge.
                    <Tooltip label={typeConfig.displayLabel} withArrow position="top">
                      <Text
                        size="10px"
                        fw={600}
                        c="dimmed"
                        style={{ whiteSpace: 'nowrap', lineHeight: '16px', height: 16 }}
                      >
                        {typeConfig.chipLabel}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Tooltip label={typeConfig.fullLabel} withArrow position="top">
                      <Badge
                        size="xs"
                        radius="sm"
                        variant={typeConfig.badgeVariant}
                        color={typeConfig.badgeColor}
                        style={{ fontWeight: 700, letterSpacing: '0.3px' }}
                      >
                        {typeConfig.chipLabel}
                      </Badge>
                    </Tooltip>
                  )}
                  <Badge
                    size="xs"
                    radius="sm"
                    variant={statusBadge.variant}
                    color={statusBadge.color}
                    style={{ fontWeight: 700, letterSpacing: '0.3px', visibility: hasDate ? 'visible' : 'hidden' }}
                  >
                    {statusBadge.label}
                  </Badge>
                  {session.attendance_marked && !session.feedback_submitted && (
                    <Tooltip label="Feedback pending" withArrow position="top">
                      <ThemeIcon size={16} radius="xl" color="warningAmber" variant="light" style={{ cursor: 'default' }}>
                        <Clock size={9} />
                      </ThemeIcon>
                    </Tooltip>
                  )}
                  {session.attendance_marked && session.feedback_submitted && (
                    <Tooltip label="Feedback submitted" withArrow position="top">
                      <ThemeIcon size={16} radius="xl" color="medleyGreen" variant="light" style={{ cursor: 'default' }}>
                        <Check size={9} />
                      </ThemeIcon>
                    </Tooltip>
                  )}
                </Group>
                )}
              </Group>
            );
          })
        )}
      </Stack>

      <Divider color="#F0EDE8" />

      {/* Footer */}
      <Group justify="space-between" px="md" py="sm" wrap="nowrap">
        <Group gap="sm" style={{ flex: 1 }} wrap="nowrap">
          <Progress
            value={progressPct}
            color={isFullyComplete ? 'medleyGreen' : 'blue'}
            size="sm"
            radius="xl"
            style={{ flex: 1, maxWidth: 120 }}
          />
          <Text size="12px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {completion.done}/{completion.total} {completion.label}
          </Text>
        </Group>
      </Group>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export type OneOnOneViewMode = 'list' | 'card';

interface OneOnOneCoachingSectionProps {
  participants: OneOnOneParticipant[];
  sessionCount: number;
  /** When true, renders as accordion with collapse + preview limit (All tab mode) */
  useAccordion?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  /** Max participants to show in accordion preview mode before "Show N more" */
  previewLimit?: number;
  onShowMore?: () => void;
  onSessionClick?: (participant: OneOnOneParticipant, session: OneOnOneSessionSlot) => void;
  /**
   * MED-1350: whether the viewer may set a date on this block. A block with no
   * date that fails the check shows "No date" and takes no click. Staff pages
   * leave it out and keep full access.
   */
  canEditSession?: (session: OneOnOneSessionSlot) => boolean;
  onAssignCoach?: (participant: OneOnOneParticipant) => void;
  onAddParticipants?: () => void;
  /** Total participant count from API (for pagination) */
  total?: number;
  /** Callback to load the next page of participants */
  onLoadMore?: () => void;
  /** Whether a "load more" request is in flight */
  loadingMore?: boolean;
  /** E18-3: Callback for "Send Calendar Invites" section-level action */
  onSendCalendarInvites?: () => void;
  /** MED-1350: the cohort has check-ins and no 1:1 modality. The subtitle reads "Check-ins". */
  checkinsOnly?: boolean;
  /**
   * MED-1360: check-ins that are on for each participant. The subtitle adds
   * them to the session count, so it matches the "1:1 Coaching" tab badge.
   */
  checkinCount?: number;
  /** Extra header buttons shown in every mode, for example booking links */
  headerActions?: ReactNode;
  /**
   * MED-1350: the list or card choice, held by a parent that stays mounted.
   * A refetch can unmount this section, and local state then resets to list.
   * Without these props the section keeps its own state.
   */
  viewMode?: OneOnOneViewMode;
  onViewModeChange?: (mode: OneOnOneViewMode) => void;
}

export function OneOnOneCoachingSection({
  participants,
  sessionCount,
  useAccordion = false,
  isExpanded = true,
  onToggle,
  previewLimit = 5,
  onShowMore,
  onSessionClick,
  canEditSession,
  onAssignCoach,
  onAddParticipants,
  total,
  onLoadMore,
  loadingMore = false,
  onSendCalendarInvites,
  checkinsOnly = false,
  checkinCount = 0,
  headerActions,
  viewMode: controlledViewMode,
  onViewModeChange,
}: OneOnOneCoachingSectionProps) {
  const [localViewMode, setLocalViewMode] = useState<OneOnOneViewMode>('list');
  const viewMode = controlledViewMode ?? localViewMode;
  const setViewMode = onViewModeChange ?? setLocalViewMode;
  // AC14: Optional type filter on CM 1:1 Coaching sub-tab
  const [typeFilter, setTypeFilter] = useState<OneOnOneType | 'all'>('all');
  // MED-1350: with no onShowMore, "Show N more" expands the preview in place,
  // so the button never does nothing.
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const collapsed = useAccordion && !previewExpanded;

  const filteredParticipants = useMemo(() => {
    // MED-1350: withdrawn rows after the others. The API already orders them
    // this way; the stable sort keeps the order inside each part.
    const sorted = [...participants].sort(
      (a, b) => Number(isWithdrawn(a)) - Number(isWithdrawn(b)),
    );
    // MED-1350: a cohort with no 1:1 modality shows check-ins only. A stray
    // paid 1:1 row (old data) adds no block and does not replace the
    // "check-ins attended" badge.
    const ordered = checkinsOnly
      ? sorted
          .map((p) => ({
            ...p,
            sessions: p.sessions.filter((s) => isCheckinOneOnOneType(s.one_on_one_type)),
            completed_count: 0,
            total_session_count: 0,
          }))
          .filter((p) => p.sessions.length > 0)
      : sorted;
    if (typeFilter === 'all') return ordered;
    return ordered
      .map((p) => ({
        ...p,
        sessions: p.sessions.filter((s) => s.one_on_one_type === typeFilter),
      }))
      .filter((p) => p.sessions.length > 0);
  }, [participants, typeFilter, checkinsOnly]);

  const displayedParticipants = useMemo(() => {
    if (collapsed && filteredParticipants.length > previewLimit) {
      return filteredParticipants.slice(0, previewLimit);
    }
    return filteredParticipants;
  }, [filteredParticipants, collapsed, previewLimit]);

  const hiddenCount = collapsed ? Math.max(0, filteredParticipants.length - previewLimit) : 0;

  const participantCount = total ?? participants.length;
  // MED-1350: a cohort with no 1:1 modality sells no 1:1s, so the section is
  // titled "Check-ins".
  const sectionTitle = checkinsOnly ? 'Check-ins' : '1:1 Coaching';
  const participantLabel = `${participantCount} participant${participantCount !== 1 ? 's' : ''}`;
  const checkinLabel = `${checkinCount} check-in${checkinCount !== 1 ? 's' : ''}`;
  const sessionsEach = sessionCount + checkinCount;
  const subtitle = checkinsOnly
    ? checkinCount > 0
      ? `${participantLabel} · ${checkinLabel} each`
      : participantLabel
    : `${participantLabel} · ${sessionsEach} session${sessionsEach !== 1 ? 's' : ''} each${
        checkinCount > 0 ? `, including ${checkinLabel}` : ''
      }`;

  const sectionHeader = (
    <Group
      justify="space-between"
      style={{
        backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
        padding: '16px 20px',
        cursor: useAccordion && onToggle ? 'pointer' : 'default',
        borderBottom: isExpanded ? '1px solid #E5E2DC' : 'none',
      }}
      onClick={useAccordion && onToggle ? onToggle : undefined}
      // MED-1354: on a narrow screen the header actions move to their own row
      // instead of squeezing the title.
      wrap="wrap"
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon color="orange" size={40} radius="md" variant="filled">
          <User size={20} />
        </ThemeIcon>
        <Box>
          <Text fw={600} size="16px" style={{ whiteSpace: 'nowrap' }}>{sectionTitle}</Text>
          <Text size="13px" c="dimmed" mt={4}>{subtitle}</Text>
        </Box>
      </Group>

      <Group gap="sm" wrap="wrap" ml="auto" maw="100%" onClick={(e) => e.stopPropagation()}>
        {headerActions}
        {/* List / Card toggle — only show in full (non-accordion) mode */}
        {!useAccordion && (
          <>
            {/* AC14: Type filter */}
            <Select
              size="xs"
              radius="sm"
              w={180}
              data={ONE_ON_ONE_TYPE_FILTER_OPTIONS}
              value={typeFilter}
              onChange={(value) => setTypeFilter((value as OneOnOneType | 'all') ?? 'all')}
              allowDeselect={false}
              aria-label="Filter by session type"
            />
            {onSendCalendarInvites && (
              <Button
                size="xs"
                radius="sm"
                variant="default"
                leftSection={<Send size={12} />}
                onClick={onSendCalendarInvites}
              >
                Send Calendar Invites
              </Button>
            )}
            {onAddParticipants && (
              <Button
                size="xs"
                radius="sm"
                variant="default"
                leftSection={<Plus size={12} />}
                onClick={onAddParticipants}
              >
                Add 1:1
              </Button>
            )}
            {/* AC8: Use existing ButtonToggle component matching sessions list/calendar toggle */}
            <ButtonToggle
              options={[
                { value: 'list' as const, icon: List, ariaLabel: 'List view' },
                { value: 'card' as const, icon: Grid, ariaLabel: 'Card view' },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
          </>
        )}

        {useAccordion && onToggle && (
          <ActionIcon variant="subtle" color="gray" onClick={onToggle}>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </ActionIcon>
        )}
      </Group>
    </Group>
  );

  const listContent = (
    <Box className={styles.list} style={{ backgroundColor: '#fff' }}>
      {displayedParticipants.length === 0 ? (
        <Text size="sm" c="dimmed" p="xl" ta="center">
          No participants enrolled in 1:1 coaching yet.
        </Text>
      ) : viewMode === 'list' || useAccordion ? (
        <>
          {displayedParticipants.map((p) => (
            <ParticipantRow
              key={p.cohort_participant_id}
              participant={p}
              onSessionClick={onSessionClick}
              canEditSession={canEditSession}
              onAssignCoach={onAssignCoach}
              onEditCoachAssignment={onAssignCoach}
            />
          ))}
        </>
      ) : (
        <Box p="md">
          {/* MED-1350: 3 columns on desktop, 2 on a tablet, 1 on a phone */}
          <Box className={styles.cardGrid}>
            {displayedParticipants.map((p) => (
              <ParticipantCard
                key={p.cohort_participant_id}
                participant={p}
                onSessionClick={onSessionClick}
                canEditSession={canEditSession}
                onAssignCoach={onAssignCoach}
              />
            ))}
          </Box>
        </Box>
      )}

      {hiddenCount > 0 && (
        <Group justify="center" py="md" style={{ borderTop: '1px solid #F0EDE8' }}>
          <Button
            variant="default"
            size="sm"
            radius="sm"
            onClick={onShowMore ?? (() => setPreviewExpanded(true))}
          >
            Show {hiddenCount} more participant{hiddenCount !== 1 ? 's' : ''}
          </Button>
        </Group>
      )}

      {!useAccordion && onLoadMore && total !== undefined && total > participants.length && (
        <Box py="sm" style={{ borderTop: '1px solid #F0EDE8', textAlign: 'center' }}>
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            onClick={onLoadMore}
            loading={loadingMore}
          >
            Show more ({total - participants.length} remaining)
          </Button>
        </Box>
      )}
    </Box>
  );

  if (useAccordion) {
    return (
      <Box style={{ border: '1px solid #E5E2DC', borderRadius: 8, overflow: 'hidden' }}>
        {sectionHeader}
        <Collapse in={isExpanded}>
          {listContent}
        </Collapse>
      </Box>
    );
  }

  // Full tab view — no accordion, header is non-collapsible
  return (
    <Box style={{ border: '1px solid #E5E2DC', borderRadius: 8, overflow: 'hidden' }}>
      {sectionHeader}
      {listContent}
    </Box>
  );
}

export default OneOnOneCoachingSection;
